import { useCallback, useRef } from 'react';

/**
 * @shared:low
 * Fire-and-forget cleanup of files attached to an entity but no longer
 * referenced in an HTML body (e.g. images removed from a TipTap editor).
 *
 * Mirrors the delete logic from FileField.removeFile:
 * 1. Save the parent entity with only the non-orphan fids
 * 2. Delete each orphan file entity via its delete_url
 */

function extractFileUrls(html) {
    const urls = new Set();
    const re = /(?:src|href)=["']([^"']+\/sites\/default\/files\/[^"']+)["']/gi;
    let match;
    while ((match = re.exec(html)) !== null) {
        const normalized = match[1].replace(/^https?:\/\/[^/]+/, '');
        urls.add(normalized);
    }
    return urls;
}

function parseUploadUrl(uploadUrl) {
    const parts = uploadUrl.replace(/^\/decoupled-api\/file\/upload\//, '').split('/');
    if (parts.length >= 3) {
        return { entType: parts[0], bundle: parts[1], fieldName: parts[2] };
    }
    return null;
}

export default function useOrphanFileCleanup(uploadUrl, entityId) {
    const uploadUrlRef = useRef(uploadUrl);
    uploadUrlRef.current = uploadUrl;
    const entityIdRef = useRef(entityId);
    entityIdRef.current = entityId;

    const cleanupOrphans = useCallback(async (bodyHtml, attachedFiles) => {
        const eid = entityIdRef.current;
        const url = uploadUrlRef.current;
        if (!eid || !url || !Array.isArray(attachedFiles) || attachedFiles.length === 0) return;

        const parsed = parseUploadUrl(url);
        if (!parsed) return;

        const bodyUrls = extractFileUrls(bodyHtml);
        const orphans = attachedFiles.filter((f) => {
            const fileUrl = f?.data?.url;
            if (!fileUrl) return false;
            const normalized = fileUrl.replace(/^https?:\/\/[^/]+/, '');
            return !bodyUrls.has(normalized);
        });

        if (orphans.length === 0) return;

        try {
            const { entType, bundle, fieldName } = parsed;
            const nonOrphanFids = attachedFiles
                .filter((f) => !orphans.includes(f))
                .filter((f) => f?.data?.fid != null)
                .map((f) => f.data.fid);

            const saveUrl = `/decoupled-api/save?display=decoupled&entity_type=${entType}&bundle=${bundle}&entity_id=${eid}`;
            await fetch(saveUrl, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: { [fieldName]: nonOrphanFids } }),
            });

            for (const orphan of orphans) {
                const deleteUrl = orphan?.delete_url;
                if (deleteUrl) {
                    fetch(deleteUrl, { method: 'POST', credentials: 'include' }).catch(() => {});
                }
            }
        } catch {
            // Fire-and-forget: don't break the save flow
        }
    }, []);

    return { cleanupOrphans };
}
